import { SECTIONS } from '@/data/sections.data'
import { getMessage } from '@/utils/messages'
import { useValuationStore } from '@/store/valuationStore'
import { useAttributeStore } from '@/store/attributeStore'
import { useUiStore } from '@/store/uiStore'
import type { ApiFieldErrors } from '@/utils/apiErrorMapper'
import { cn } from '@/utils/cn'
import { FIELD_VALIDATORS } from '@/utils/fieldValidators'
import {
  TRUCK_STANDARD_FIELDS,
  TRAILER_STANDARD_FIELDS,
  TRUCK_ADDITIONAL_FIELDS,
  TRAILER_ADDITIONAL_FIELDS,
  TRUCK_CONDITIONAL_FIELDS,
  TRAILER_CONDITIONAL_FIELDS,
} from '@/data/conditionalFields.data'
import { normaliseToKey } from '@/data/refCategories'
import { mapAttributesToFields } from '@/utils/attributeMapper'
import type { AttributeDefinition } from '@/services/attributeService'
import type { FieldDefinition } from '@/types'

/**
 * Checks whether a section has any unfilled required fields,
 * using the same conditional-field resolution as useConditionalFields.
 */
const SAVE_REQUIRED_FIELDS = new Set(['claimRefNo', 'vehicleType'])

function sectionHasError(
  sectionId: string,
  formValues: Record<string, Record<string, string>>,
  hasTriedSubmit: boolean,
  hasTriedSave: boolean,
  attrCache: Record<string, AttributeDefinition[]>,
  apiFieldErrors: ApiFieldErrors = {}
): boolean {
  if (!hasTriedSubmit && !hasTriedSave && !Object.keys(apiFieldErrors[sectionId] ?? {}).length) return false

  const vehicleType = formValues['basic-information']?.['vehicleType'] ?? ''
  const useValue    = formValues['standard-equipment']?.['use']         ?? ''
  const typeValue   = formValues['standard-equipment']?.['type']        ?? ''

  let fields: FieldDefinition[] = SECTIONS.find(s => s.id === sectionId)?.fields ?? []

  if (sectionId === 'standard-equipment') {
    fields = vehicleType === 'Truck/Tractor' ? TRUCK_STANDARD_FIELDS
           : vehicleType === 'Trailer'       ? TRAILER_STANDARD_FIELDS
           : []
  } else if (sectionId === 'additional-options') {
    fields = vehicleType === 'Truck/Tractor' ? TRUCK_ADDITIONAL_FIELDS
           : vehicleType === 'Trailer'       ? TRAILER_ADDITIONAL_FIELDS
           : []
  } else if (sectionId === 'body-information') {
    if (vehicleType === 'Truck/Tractor') {
      const isValidUse = useValue && useValue !== 'Select'
      if (isValidUse) {
        const attrs = attrCache[`TRUCK_BODY_${normaliseToKey(useValue)}`]
        fields = attrs?.length ? mapAttributesToFields(attrs) : []
      } else {
        fields = []
      }
    } else if (vehicleType === 'Trailer') {
      const isValidType = typeValue && typeValue !== 'Select'
      if (isValidType) {
        const attrs = attrCache[`TRAILER_BODY_${normaliseToKey(typeValue)}`]
        fields = attrs?.length ? mapAttributesToFields(attrs) : []
      } else {
        fields = []
      }
    }
  } else if (sectionId === 'vehicle-condition') {
    fields = vehicleType === 'Truck/Tractor' ? TRUCK_CONDITIONAL_FIELDS
           : vehicleType === 'Trailer'       ? TRAILER_CONDITIONAL_FIELDS
           : []
  }

  const vals = formValues[sectionId] ?? {}
  const hasSubmitError = hasTriedSubmit && fields
    .filter(f => f.required)
    .some(f => !vals[f.id] || vals[f.id].startsWith('Select') || !!FIELD_VALIDATORS[f.id]?.(vals[f.id], vals))
  const hasSaveError = hasTriedSave && fields
    .some(f => SAVE_REQUIRED_FIELDS.has(f.id) && (!vals[f.id] || vals[f.id].startsWith('Select')))
  const visibleFieldIds = new Set(fields.map(f => f.id))
  const hasApiError = Object.keys(apiFieldErrors[sectionId] ?? {}).some(id => visibleFieldIds.has(id))
  return hasSubmitError || hasSaveError || hasApiError
}

export function Sidebar() {
  const { sectionStates, toggleSection, formValues, hasTriedSubmit, hasTriedSave } = useValuationStore()
  const { sidebarCollapsed, toggleSidebar, apiFieldErrors } = useUiStore()
  const attrCache = useAttributeStore((s) => s.cache)

  return (
    <>
      {/* ── Desktop sidebar (md+) ─────────────────────────────── */}
      <aside
        className={cn(
          'hidden md:block',
          'bg-[var(--surface)] border-r border-[var(--border)]',
          'sticky top-[60px] h-[calc(100vh-60px)]',
          'overflow-hidden transition-[width] duration-[280ms] ease-[cubic-bezier(0.4,0,0.2,1)]',
          'flex-shrink-0'
        )}
        style={{ width: sidebarCollapsed ? 50 : 252 }}
      >
        {/* Inner wrapper — fixed width so content doesn't reflow during collapse */}
        <div className="flex flex-col h-full" style={{ width: 252 }}>

          {/* ── Top row: toggle button FIRST so it stays within the
               50 px collapsed strip, then the label fades out ── */}
          <div className="flex items-center gap-[10px] px-3 py-[13px] border-b border-[var(--border)] flex-shrink-0">
            <button
              onClick={toggleSidebar}
              className={cn(
                'w-[26px] h-[26px] rounded-[6px] border border-[var(--border)] bg-[var(--surface2)]',
                'cursor-pointer flex items-center justify-center flex-shrink-0',
                'text-[var(--text-muted)] text-[13px]',
                'hover:border-[var(--accent)] hover:text-[var(--accent)] hover:bg-[rgba(61,142,240,0.07)]',
                'transition-all duration-200 select-none'
              )}
              title={sidebarCollapsed ? getMessage('sidebar.tooltip_expand') : getMessage('sidebar.tooltip_collapse')}
            >
              {sidebarCollapsed ? '▷' : '◁'}
            </button>

            <span
              className={cn(
                'text-[12px] font-bold overflow-hidden text-ellipsis',
                'transition-[opacity,max-width] duration-[180ms]',
                sidebarCollapsed ? 'opacity-0 max-w-0' : 'opacity-100 max-w-[160px]'
              )}
              style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}
            >
              {getMessage('sidebar.label_sections')}
            </span>
          </div>

          {/* ── Nav items ───────────────────────────────────────── */}
          {/* pb-[76px] ensures the last item scrolls above the fixed Save/Submit bar (~70px tall) */}
          <nav className="p-2 pb-[76px] overflow-y-auto overflow-x-hidden flex-1">
            {SECTIONS.map((section) => {
              const state = sectionStates[section.id]
              const isActive   = state?.expanded ?? false
              const isComplete = false
              const hasError   = sectionHasError(section.id, formValues, hasTriedSubmit, hasTriedSave, attrCache, apiFieldErrors)

              return (
                <div
                  key={section.id}
                  onClick={() => toggleSection(section.id)}
                  title={section.name}
                  className={cn(
                    'flex items-center gap-[10px] py-[9px] rounded-[var(--radius-sm)]',
                    sidebarCollapsed ? 'px-[1px]' : 'px-[10px]',
                    'cursor-pointer transition-all duration-[180ms] mb-[2px]',
                    'whitespace-nowrap overflow-hidden',
                    !sidebarCollapsed && 'border border-transparent',
                    !sidebarCollapsed && isActive    && 'bg-[var(--section-active)] border-[var(--accent)]',
                    !sidebarCollapsed && isComplete  && !isActive && 'bg-[var(--section-complete)] border-[rgba(34,197,94,0.3)]',
                    !sidebarCollapsed && hasError    && !isActive && 'border-[rgba(220,38,38,0.35)] bg-[rgba(220,38,38,0.04)]',
                    sidebarCollapsed  && isActive    && 'bg-[var(--section-active)]',
                    sidebarCollapsed  && isComplete  && !isActive && 'bg-[var(--section-complete)]',
                    sidebarCollapsed  && hasError    && !isActive && 'bg-[rgba(220,38,38,0.04)]',
                    !isActive   && !isComplete && !hasError && 'hover:bg-[var(--surface2)] hover:border-[var(--border)]'
                  )}
                >
                  {/* Number bubble */}
                  <div
                    className={cn(
                      'w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center',
                      'text-[13px] font-semibold transition-all duration-200',
                      !sidebarCollapsed && 'border',
                      isActive   ? 'bg-[var(--accent)] border-[var(--accent)] text-white'
                      : isComplete ? 'bg-[var(--success)] border-[var(--success)] text-white'
                      : hasError   ? 'bg-[rgba(220,38,38,0.1)] border-[var(--error)] text-[var(--error)]'
                      : ''
                    )}
                    style={!isActive && !isComplete && !hasError
                      ? { backgroundColor: `${section.iconColor}1f`, borderColor: sidebarCollapsed ? 'transparent' : `${section.iconColor}66`, color: section.iconColor }
                      : undefined}
                  >
                    {isComplete ? '✓' : section.icon}
                  </div>

                  {/* Label */}
                  <span
                    className={cn(
                      'text-[12px] font-bold overflow-hidden text-ellipsis',
                      'transition-[opacity,max-width] duration-[180ms]',
                      isActive  ? 'text-[var(--accent)]'
                      : hasError ? 'text-[var(--error)]'
                      : 'text-[var(--text)]',
                      sidebarCollapsed ? 'opacity-0 max-w-0' : 'opacity-100 max-w-[180px]'
                    )}
                    style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}
                  >
                    {section.name}
                  </span>
                </div>
              )
            })}
          </nav>
        </div>
      </aside>

      {/* ── Mobile bottom nav (< md) ──────────────────────────── */}
      <nav
        className={cn(
          'md:hidden fixed bottom-0 left-0 right-0 z-[250]',
          'bg-white/95 border-t border-[var(--border)] backdrop-blur-[12px]',
          'flex items-stretch overflow-x-auto safe-nav-h',
          // Hide scrollbar but keep scroll functionality
          '[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]'
        )}
      >
        {SECTIONS.map((section) => {
          const state     = sectionStates[section.id]
          const isActive  = state?.expanded ?? false
          const isComplete = false
          const hasError  = sectionHasError(section.id, formValues, hasTriedSubmit, hasTriedSave, attrCache)

          return (
            <button
              key={section.id}
              type="button"
              onClick={() => toggleSection(section.id)}
              className={cn(
                'flex flex-col items-center justify-center gap-[3px]',
                'flex-1 min-w-0 px-1',
                'text-center transition-all duration-150 relative',
                isActive   ? 'text-[var(--accent)]'
                : hasError  ? 'text-[var(--error)]'
                : 'text-[var(--text-muted)]'
              )}
            >
              {/* Active indicator bar */}
              {isActive && (
                <span className="absolute top-0 left-3 right-3 h-[2px] rounded-b-full bg-[var(--accent)]" />
              )}
              {hasError && !isActive && (
                <span className="absolute top-0 left-3 right-3 h-[2px] rounded-b-full bg-[var(--error)]" />
              )}

              {/* Icon bubble */}
              <div
                className={cn(
                  'w-[38px] h-[38px] rounded-full flex items-center justify-center text-[18px]',
                  'border transition-all duration-150',
                  isActive   ? 'bg-[var(--accent)] border-[var(--accent)] text-white'
                  : hasError  ? 'bg-[rgba(220,38,38,0.1)] border-[var(--error)] text-[var(--error)]'
                  : ''
                )}
                style={!isActive && !hasError
                  ? { backgroundColor: `${section.iconColor}1f`, borderColor: `${section.iconColor}66`, color: section.iconColor }
                  : undefined}
              >
                {isComplete ? '✓' : section.icon}
              </div>

              {/* Label hidden on mobile — nav is icons-only */}
              <span className="hidden text-[9px] font-semibold tracking-[0.2px] leading-none w-full truncate text-center">
                {section.name}
              </span>
            </button>
          )
        })}
      </nav>
    </>
  )
}
